import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import ProjectCategories from '../../constants/projectCategories';
import '../../styles/admin/EditProjects.css';
import ProtectedAdminRoute from '../../components/ProtectedAdminRoute';
import FileUpload from '../../components/FileUpload';

// Removed BACKEND_URL, use apiFetch instead

// Use ProjectCategories.OPTIONS for category choices

const statusOptions = [
  'Active',
  'Completed',
  'On Hold',
  'Archived'
];

export default function EditProject() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [allNames, setAllNames] = useState([]); // All project names for datalist
  const [results, setResults] = useState([]);
  const [form, setForm] = useState(null);
  const [message, setMessage] = useState('');

  // Fetch all project names on mount
  useEffect(() => {
    apiFetch('/api/projects')
      .then(data => setAllNames(data.projects.map(p => p.name)));
  }, []);

  // Fetch project details as user types (for selection)
  useEffect(() => {
    if (search.length < 1) {
      setResults([]);
      return;
    }
    apiFetch(`/api/projects?name=${encodeURIComponent(search)}`)
      .then(data => setResults(data.projects || []));
  }, [search]);

  const handleSelect = async (name) => {
    // Always fetch the exact project by name
    const data = await apiFetch(`/api/projects?name=${encodeURIComponent(name)}`);
    const project = data.projects.find(p => p.name === name);
    if (project) {
      // Ensure sections array exists and has proper structure
      const formattedProject = {
        ...project,
        originalName: project.name, // Store original name for API calls
        sections: project.sections || [],
        status: project.status || 'Active',
        githubUrl: project.githubUrl || '',
        liveUrl: project.liveUrl || '',
        pdfUrl: project.pdfUrl || '',
        introduction: project.introduction || project.description || '' // Support legacy projects
      };
      setForm(formattedProject);
      setMessage('');
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSectionChange = (index, field, value) => {
    setForm(form => ({
      ...form,
      sections: form.sections.map((section, i) => 
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  const handleSectionImageChange = (sectionIndex, imageIndex, value) => {
    setForm(form => ({
      ...form,
      sections: form.sections.map((section, i) => 
        i === sectionIndex ? {
          ...section,
          images: section.images.map((img, j) => j === imageIndex ? value : img)
        } : section
      )
    }));
  };

  const addSection = () => {
    if (form.sections.length < 10) { // Limit to 10 sections
      setForm(form => ({
        ...form,
        sections: [...form.sections, {
          subtitle: '',
          body: '',
          images: ['']
        }]
      }));
    }
  };

  const removeSection = (index) => {
    setForm(form => ({
      ...form,
      sections: form.sections.filter((_, i) => i !== index)
    }));
  };

  const addImageToSection = (sectionIndex) => {
    const section = form.sections[sectionIndex];
    if (section.images.length < 3) { // Limit to 3 images per section
      setForm(form => ({
        ...form,
        sections: form.sections.map((section, i) => 
          i === sectionIndex ? {
            ...section,
            images: [...section.images, '']
          } : section
        )
      }));
    }
  };

  const removeImageFromSection = (sectionIndex, imageIndex) => {
    setForm(form => ({
      ...form,
      sections: form.sections.map((section, i) => 
        i === sectionIndex ? {
          ...section,
          images: section.images.filter((_, j) => j !== imageIndex)
        } : section
      )
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form) return;
    // ...validation code unchanged...
    const originalName = form.originalName || form.name; // Use original name for the API call
    const projectData = {
      ...form,
      sections: form.sections.map(section => ({
        subtitle: section.subtitle,
        body: section.body,
        images: section.images.filter(img => img.trim() !== '') // Remove empty image URLs
      }))
    };
    const data = await apiFetch(`/api/projects/${originalName}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(projectData),
    });
    if (data && data.name) {
      setMessage('Project updated!');
      setTimeout(() => navigate(`/projects/${data.name}`), 1000);
    } else {
      setMessage(`Error updating project: ${data && data.message ? data.message : 'Unknown error'}`);
    }
  };

  return (
    <ProtectedAdminRoute>
      <div className="add-project-flex">
      <div className="edit-project-form">
        <h2>Edit Project</h2>
        <input
          type="text"
          placeholder="Search project by name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          list="project-names"
          autoComplete="off"
        />
        <datalist id="project-names">
          {allNames.map(name => (
            <option key={name} value={name} />
          ))}
        </datalist>
        {search && results.some(p => p.name === search) && !form && (
          <button
            style={{ marginBottom: '1rem' }}
            onClick={() => handleSelect(search)}
            type="button"
          >
            Edit "{search}"
          </button>
        )}
        {form && (
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="form-group">
              <label htmlFor="name">Project Name (URL-friendly)</label>
              <input
                id="name"
                name="name"
                placeholder="project-name"
                value={form.name}
                onChange={handleChange}
                required
                autoComplete="off"
                pattern="[a-z0-9-]+"
                title="Only lowercase letters, numbers, and hyphens allowed"
              />
              <small className="form-help">This is used in the URL. Changing this will change the project's web address.</small>
            </div>

            <div className="form-group">
              <label htmlFor="displayName">Display Name</label>
              <input
                id="displayName"
                name="displayName"
                placeholder="Project Display Name"
                value={form.displayName}
                onChange={handleChange}
                required
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <FileUpload
                label="Main Project Image"
                currentUrl={form.image}
                onUpload={(url) => setForm({...form, image: url})}
                accept="image/*"
                showPreview={true}
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                className="edit-project-select"
                value={form.category}
                onChange={handleChange}
                required
                autoComplete="off"
              >
                <option value="" disabled>Select category</option>
                {ProjectCategories.OPTIONS.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                className="edit-project-select"
                value={form.status || 'Active'}
                onChange={handleChange}
                autoComplete="off"
              >
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="introduction">Introduction</label>
              <textarea
                id="introduction"
                name="introduction"
                placeholder="Brief introduction or summary of the project..."
                value={form.introduction}
                onChange={handleChange}
                rows={3}
                className="form-textarea"
                required
                autoComplete="off"
                maxLength={500}
              />
              <div className={`character-count ${
                form.introduction.length > 450 ? 'error' : 
                form.introduction.length > 400 ? 'warning' : ''
              }`}>
                {form.introduction.length}/500 characters
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="githubUrl">GitHub URL (Optional)</label>
              <input
                id="githubUrl"
                name="githubUrl"
                placeholder="https://github.com/username/repository"
                value={form.githubUrl || ''}
                onChange={handleChange}
                type="url"
                pattern="https?://.+"
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label htmlFor="liveUrl">Live Demo URL (Optional)</label>
              <input
                id="liveUrl"
                name="liveUrl"
                placeholder="https://your-project-demo.com"
                value={form.liveUrl || ''}
                onChange={handleChange}
                type="url"
                pattern="https?://.+"
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <FileUpload
                label="Project PDF Document (Optional)"
                currentUrl={form.pdfUrl}
                onUpload={(url) => setForm({...form, pdfUrl: url})}
                accept="application/pdf"
                showPreview={true}
              />
            </div>

            {/* Dynamic Sections */}
            <div className="sections-container">
              <div className="sections-header">
                <h3>Article Content</h3>
              </div>

              {form.sections.map((section, sectionIndex) => (
                <div key={sectionIndex} className="project-section">
                  <div className="section-header">
                    <h4>Content Block {sectionIndex + 1}</h4>
                    <button 
                      type="button" 
                      onClick={() => removeSection(sectionIndex)}
                      className="remove-section-btn"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="form-group">
                    <label>Heading (Optional)</label>
                    <input
                      type="text"
                      placeholder="Section heading or subheading..."
                      value={section.subtitle}
                      onChange={(e) => handleSectionChange(sectionIndex, 'subtitle', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Paragraph Content</label>
                    <textarea
                      placeholder="Write your article content here. Each line will become a separate paragraph..."
                      value={section.body}
                      onChange={(e) => handleSectionChange(sectionIndex, 'body', e.target.value)}
                      rows={6}
                      className="form-textarea"
                      required
                    />
                  </div>

                  <div className="section-images">
                    <div className="images-header">
                      <label>Images (Optional)</label>
                      <button 
                        type="button" 
                        onClick={() => addImageToSection(sectionIndex)}
                        className="add-image-btn"
                        disabled={section.images.length >= 3}
                      >
                        + Add Image
                      </button>
                    </div>

                    {section.images.map((image, imageIndex) => (
                      <div key={imageIndex} className="image-input-group">
                        <FileUpload
                          label={`Image ${imageIndex + 1}`}
                          currentUrl={image}
                          onUpload={(url) => handleSectionImageChange(sectionIndex, imageIndex, url)}
                          accept="image/*"
                          showPreview={true}
                        />
                        {section.images.length > 1 && (
                          <button 
                            type="button" 
                            onClick={() => removeImageFromSection(sectionIndex, imageIndex)}
                            className="remove-image-btn"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <button 
                type="button" 
                onClick={addSection}
                className="add-section-btn"
                disabled={form.sections.length >= 10}
              >
                + Add Content Block
              </button>
            </div>

            <button type="submit" className="submit-btn">Save Changes</button>
            
            <button
              type="button"
              className="delete-btn"
              onClick={async () => {
                if (
                  window.confirm(
                    `Are you sure you want to delete "${form.displayName || form.name}"? This cannot be undone.`
                  )
                ) {
                  const data = await apiFetch(`/api/projects/${form.name}`, {
                    method: "DELETE",
                  });
                  if (data && data.success) {
                    setMessage("Project deleted!");
                    setForm(null);
                    setSearch('');
                    setTimeout(() => navigate("/projects"), 1000);
                  } else {
                    setMessage("Error deleting project");
                  }
                }
              }}
            >
              Remove Project
            </button>
          </form>
        )}
        
        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
      </div>
    </div>
    </ProtectedAdminRoute>
  );
}